import { useEffect, useRef } from 'react';
import { useMyContexts } from './use-my-contexts';
import { useSwitchContext } from './use-switch-context';
import { useContextStore, activeContextStorage } from './context-store';
import type { ResolvedMembership } from './session.types';

export interface ContextBootstrap {
  /** The contexts the user may act in (for the picker). */
  contexts: ResolvedMembership[];
  /** Still fetching the context list. */
  isLoading: boolean;
  /** The context list itself failed to load. */
  isError: boolean;
  /** A `switchContext` is in flight (auto or user-driven). */
  isSwitching: boolean;
  /** Retry the context list. */
  refetch: () => void;
  /** Activate a context (used by the picker). */
  selectContext: (contextTenantId: string) => void;
}

/**
 * Drive the active-context lifecycle (T17). On login the user has no active
 * context; once `myContexts` loads we activate one via `switchContext` (the only
 * way to write the server session):
 *   - restore the persisted tenant id if it's still in the list, else
 *   - auto-pick the FIRST context (no forced picker on login).
 * The picker only appears when the user has no contexts at all, or when the
 * auto-switch fails / `ACL_CONTEXT_REQUIRED` clears the active context.
 *
 * The auto-activation runs at most once per mount (a denied/failed switch leaves
 * `active` null → the picker). `switchContext` itself mirrors the result into the
 * store, persists it, and invalidates the cache.
 */
export function useContextBootstrap(): ContextBootstrap {
  const contextsQuery = useMyContexts();
  const active = useContextStore((s) => s.active);
  const switchContext = useSwitchContext();
  const attempted = useRef(false);

  const list = contextsQuery.data;

  useEffect(() => {
    if (active || attempted.current || !list) return;
    attempted.current = true;

    const stored = activeContextStorage.get();
    const restore = stored ? list.find((c) => c.contextTenantId === stored) : undefined;
    // Persisted context wins; otherwise auto-pick the first so login never
    // stalls on a picker. Zero contexts → the gate renders the picker's empty state.
    const target = restore ?? list[0];

    if (target) switchContext.mutate(target.contextTenantId);
  }, [active, list, switchContext]);

  return {
    contexts: list ?? [],
    isLoading: contextsQuery.isLoading,
    isError: contextsQuery.isError,
    isSwitching: switchContext.isPending,
    refetch: () => void contextsQuery.refetch(),
    selectContext: (contextTenantId) => switchContext.mutate(contextTenantId),
  };
}
